import React from 'react';
import { bool, func, string } from 'prop-types';

import { isEmpty, get, orderBy } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import {connect} from 'react-redux';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';

import EsmsDialog from '../../components/EsmsDialog';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as ArchitectActions from '../../actions/architectActions';
import * as HomeActions from '../../actions/homeActions';
import * as SubscriptionActions from '../../actions/subscriptionActions';
import * as UiActions from '../../actions/uiActions';

import classes from './SubscriptionModal.module.css';

const ValidationSchema = Yup.object().shape({
    email: Yup.boolean(),
    sms:   Yup.boolean()
});

const EditSubscriptionModal = ({
    close,
    toggleLoading,
    clearErrors,
    errors,
    selectedSubscription,
    title,
    open,
    isLoading,
    setSubscription,
    ...props
}) => {

    const formik = useFormik({
        initialValues: {
            email: get(selectedSubscription, 'email', false),
            sms:   get(selectedSubscription, 'sms', false)
        },
        validationSchema: ValidationSchema,
        onSubmit:         async (values, { setSubmitting }) => {
            const {
                updateSubscription,
                currentUserId,
            } = props;

            let isUpdated;

            let body = {
                subscribers: [
                    {
                        subscribertype: selectedSubscription.subscriberType.toLowerCase(),
                        subscriberid:   selectedSubscription.subscriberId,
                        id:             selectedSubscription.id
                    }
                ],
                email:    values.email,
                entityid: selectedSubscription.entityId,
                sms:      values.sms,
                userid:   currentUserId
            };

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isUpdated = await updateSubscription(body);
                }

                if (isUpdated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const prepareTitle = () => {
        if (!isEmpty(selectedSubscription.target)) {

            const parents = selectedSubscription.target.split('/');

            return parents[parents.length - 1];
        }

        return 'Subscription';
    };

    const prepareSubtitle = () => {
        if (!isEmpty(selectedSubscription)) {
            switch (selectedSubscription.entityType) {
                case 'esmscompany':
                    return 'Company';
                case 'esmsclient':
                    return 'Client';
                case 'esmssite':
                    return 'Site';
                case 'esmsequipment':
                    return 'Equipment';
                case 'esmssystem':
                    return 'System';
                case 'esmscampus':
                    return 'Campus';
                case 'esmsarea':
                    return 'Area';
                default:
                    return '';
            }
        }
        return 'BOB Entity';
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        setSubscription(null);
        close();
    };

    if (!selectedSubscription) return null;

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Update"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/RingingAlarmFlat.svg" alt="subscription icon" />
                        <span>Subscription</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Subscribing allows you to receive alerts for this entity as well as any children.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <h3 className={classes.subsectionTitle}>{prepareTitle()}</h3>
                        <span className={classes.subsectionSubTitle}>{prepareSubtitle()}</span>
                        {
                            selectedSubscription.ruleName ?
                                <div className={classes.input}>
                                    <h3 className={classes.subsectionTitle}>{selectedSubscription.ruleName}</h3>
                                    <span className={classes.subsectionSubTitle}>Rule</span>
                                </div>
                                : null
                        }
                    </div>
                    <div className={classes.inputs}>
                        <FormControl className={classes.checkboxWrapper}>
                            <FormLabel
                                component="legend"
                                className={classes.checkboxGroupLabel}
                            >
                                Subscription Method
                            </FormLabel>
                            <FormGroup row>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik.values.email}
                                            value="email"
                                            name="email"
                                            color="primary"
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label="Email"
                                    className={classes.checkboxLabel}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik.values.sms}
                                            value="sms"
                                            name="sms"
                                            color="primary"
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label="SMS"
                                    className={classes.checkboxLabel}
                                />
                            </FormGroup>
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

EditSubscriptionModal.defaultProps = {
    close: () => { },
    open:  false,
    title: ''
};

EditSubscriptionModal.propTypes = {
    close: func.isRequired,
    open:  bool.isRequired,
    title: string.isRequired
};

const mapStateToProps = state => {
    const {
        contacts,
        order,
        orderBy: orderByKey
    } = state.architect;

    return {
        companyInfo:          state.companyInfo,
        contacts:             orderBy(contacts, [orderByKey], order),
        currentUserId:        state.currentUser.id,
        errors:               state.messages.errors,
        isLoading:            state.ui.isLoading,
        selectedSite:         state.site.selectedSite,
        selectedSubscription: state.subscription.selectedSubscription
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...ArchitectActions,
    ...HomeActions,
    ...SubscriptionActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditSubscriptionModal);
